import React, { useRef, useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { select } from '@panel/commons/core/themev2';
import { getColor } from '@panel/commons/core/themev3';
import EmptyStateImg from '@panel/commons/assets/img/il-empty-state.svg';
import EmptyStateImgUB from '@panel/commons/assets/img/il-empty-state-ub.svg';
import { momentFormaterISO } from '@panel/commons/helpers';
import { crmFinishReasonsIds } from '@panel/commons/helpers/CRMOptionsConfig';

const siteId = process.env.REACT_APP_BUILD_SITE;

const HistoryModal = ({ postingLogs, onClose, publisherTypeId }) => {
  const { t } = useTranslation();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [buttonVisible, setButtonVisible] = useState(false);
  const [formatedLogs, setFormatedLogs] = useState([]);
  const noData = !postingLogs || postingLogs.length === 0;
  const formatDate = d => `${momentFormaterISO(d, 'DD/MM')} ${momentFormaterISO(d, 'HH:mm')}hrs`;
  const logsAreaRef = useRef();
  const isProfessional = publisherTypeId !== '1';
  const publisherType = isProfessional ? 'profesionales' : 'particulares';

  const formatLogs = logs => {
    const result = [];
    logs.forEach(log => {
      if (!crmFinishReasonsIds.includes(log.finish_reason)) return result.push(log);
      return result.push({ ...log, posting_user: 'Usuario de navent' });
    });
    return result;
  };

  useEffect(() => {
    // se formatean los logs para acciones de ATC
    if (postingLogs) {
      setFormatedLogs(formatLogs(postingLogs));
    }
  }, [postingLogs]);

  useEffect(() => {
    if (logsAreaRef && logsAreaRef.current) {
      if (logsAreaRef.current.scrollHeight > logsAreaRef.current.clientHeight) {
        setIsOverflowing(true);
        logsAreaRef.current.addEventListener('scroll', () => {
          if (logsAreaRef.current.scrollTop < 0.5) {
            setButtonVisible(false);
          }
          if (logsAreaRef.current.scrollTop > 0 && !buttonVisible) {
            setButtonVisible(true);
          }
        });
      }
    }
  }, [buttonVisible, logsAreaRef]);

  const memoizedScrollToTop = useCallback(() => {
    if (logsAreaRef.current) {
      logsAreaRef.current.scrollTo(0, 0);
    }
  }, [logsAreaRef]);

  const getDescription = item => {
    const isFraudOrInfrigementWithDesc =
      (item.finish_reason === 'fraud' || item.finish_reason === 'infringement') && item.finish_reason_description;
    if (item.finish_reason === 'other' || isFraudOrInfrigementWithDesc) return item.finish_reason_description;
    if (crmFinishReasonsIds.includes(item.finish_reason))
      return t(`panel.avisos.suspendPublication.motivo.${item.finish_reason}`);
    return t(`panel.avisos.suspendPublication.motivo.${publisherType}.${item.finish_reason}`);
  };

  const getEmptyStateImage = () => {
    if (siteId === 'UBPE') return EmptyStateImgUB;
    return EmptyStateImg;
  };

  return (
    <Container data-panel='panel-modal-historyPost-container'>
      {noData && <Img src={getEmptyStateImage()} data-panel='panel-modal-historyPost-image' />}
      <Title data-panel='panel-modal-historyPost-title'>
        {!noData ? t('panel.posting.modal.log.title') : t('panel.posting.modal.log.emptyState.title')}
      </Title>
      <Text empty={noData} data-panel='panel-modal-historyPost-text'>
        {!noData ? t('panel.posting.modal.log.data') : t('panel.posting.modal.log.emptyState.text')}
      </Text>
      {!noData && (
        <ContentLayout
          isOverflowing={isOverflowing}
          className={`${buttonVisible && isOverflowing && 'top-fade'} ${!buttonVisible &&
            isOverflowing &&
            'bottom-fade'}`}
          ref={logsAreaRef}
        >
          {formatedLogs.map((item, i) => {
            return (
              // eslint-disable-next-line react/no-array-index-key
              <LogItem key={i} data-panel='activity-item-container'>
                <div className='left-side'>
                  <div className='ball-container'>
                    <div className='center-ball' />
                  </div>
                  {i !== formatedLogs.length - 1 && <div className='dashed-line' />}
                </div>
                <div className='righ-side'>
                  <p className='logItem-description' data-panel='activity-item-description'>
                    {item.posting_user.trim()} {t(`panel.desempeno.activity.log.${item.action}`)}
                  </p>
                  {item.finish_reason && (
                    <p className='logItem-motivo'>
                      Motivo: &quot;
                      {getDescription(item)}
                      &quot;
                    </p>
                  )}
                  <p className='logItem-date' data-panel='activity-item-date'>
                    {formatDate(item.execution_date)}
                  </p>
                </div>
              </LogItem>
            );
          })}
        </ContentLayout>
      )}

      <UpButtonContainer>
        <Button noData={noData} onClick={() => onClose()} data-panel='panel-modal-historyPost-button'>
          {t('panel.posting.modal.log.close')}
        </Button>
        <GoTopButton
          onClick={memoizedScrollToTop}
          visible={buttonVisible}
          data-panel='panel-modal-historyPost-button-ctaUp'
        >
          {t('panel.posting.modal.log.cta.up')}
        </GoTopButton>
      </UpButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  padding: 28px;
  box-sizing: border-box;
  min-height: 300px;
  max-height: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div`
  font-family: Hind;
  font-size: 20px;
  font-weight: 600;
  line-height: 26px;
  white-space: pre-wrap;
  margin-bottom: 16px;
`;

const Text = styled.div`
  font-family: Hind;
  font-size: 16px;
  font-weight: 400;
  line-height: 26px;
  color: ${select('darkGray')};
  white-space: pre-wrap;
  text-align: center;
  max-width: ${({ empty }) => empty && '300px'};
`;

const Img = styled.div`
  background-image: url(${({ src }) => src});
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  width: 137px;
  height: 154px;
  margin-top: 25px;
  margin-bottom: 45px;
`;

const ContentLayout = styled.div`
  padding: 24px 8px;
  padding-bottom: ${({ isOverflowing }) => !isOverflowing && '0'};
  overflow-y: auto;
  width: 100%;
  ::-webkit-scrollbar {
    -webkit-appearance: none;
  }
  ::-webkit-scrollbar:vertical {
    width: 4px;
  }
  ::-webkit-scrollbar-thumb {
    border-radius: 32px;
    border: 8px solid ${select('midDarkGray')};
  }
  &.bottom-fade {
    -webkit-mask-image: linear-gradient(to bottom, black 75%, transparent 100%);
    mask-image: linear-gradient(to bottom, black 75%, transparent 100%);
  }
  &.top-fade {
    -webkit-mask-image: linear-gradient(to top, black 75%, transparent 100%);
    mask-image: linear-gradient(to top, black 75%, transparent 100%);
  }
`;

const LogItem = styled.div`
  min-height: 70px;
  display: flex;
  .left-side {
    width: 7.5%;
    .ball-container {
      background-color: #c7dafb;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      margin-top: 5px;
      display: flex;
      align-items: center;
      justify-content: center;
      .center-ball {
        background-color: #7494c8;
        width: 6px;
        height: 6px;
        border-radius: 50%;
      }
    }
    .dashed-line {
      width: 5px;
      height: 100%;
      border-left: 2px dotted #c7dafb;
      margin-left: 5px;
    }
  }
  .righ-side {
    width: 85%;
  }
  .logItem-description {
    color: ${select('blueDark')};
    font-weight: 400;
    font-size: 14px;
    white-space: break-spaces;
  }
  .logItem-motivo {
    color: ${select('midDarkGray')};
    font-family: 'Hind';
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    max-width: 300px;
    white-space: break-spaces;
  }
  .logItem-date {
    color: ${select('midDarkGray')};
    font-weight: 500;
    font-size: 12px;
  }
`;

const UpButtonContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: flex-end;
`;

const Button = styled.button`
  height: 40px;
  min-width: 114px;
  border-radius: 8px;
  border: 1px solid ${getColor('primary')};
  font-family: Hind;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  cursor: pointer;
  :hover {
    background-color: ${getColor('tertiary')};
  }
  ${({ noData }) =>
    noData &&
    css`
      background-color: ${getColor('primary')};
      color: white;
      width: 100%;
      margin-top: 42px;
      font-size: 16px;
      :hover {
        background-color: ${getColor('secondary')};
      }
    `}
`;

const GoTopButton = styled(Button)`
  width: 114px;
  display: ${({ visible }) => (visible ? 'block' : 'none')};
  background-color: ${getColor('primary')};
  color: white;
  margin-left: 15px;
`;

export default HistoryModal;
